import { Calendar, ChevronDown, RotateCcw, Search, SlidersHorizontal, X } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import DatePicker from "@/components/ui/DatePicker";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import { AUTH_MODE_OPTIONS, MANDATE_SORT_OPTIONS } from "@/components/feature/mandates/constants";
import { RANGES } from "../data";

interface Chip {
  label: string;
  onRemove: () => void;
}

interface FilterBarProps {
  search: string;
  onSearch: (v: string) => void;
  range: string;
  onRange: (v: string) => void;
  customFrom: string;
  customTo: string;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
  bank: string;
  onBank: (v: string) => void;
  banks: string[];
  mode: string;
  onMode: (v: string) => void;
  sort: string;
  onSort: (v: string) => void;
  onReset: () => void;
  chips: Chip[];
}

export default function FilterBar({
  search,
  onSearch,
  range,
  onRange,
  customFrom,
  customTo,
  onCustomFrom,
  onCustomTo,
  bank,
  onBank,
  banks,
  mode,
  onMode,
  sort,
  onSort,
  onReset,
  chips,
}: FilterBarProps) {
  const [rangeOpen, setRangeOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const rangeButton = (
    <button
      type="button"
      onClick={() => setRangeOpen((v) => !v)}
      className="inline-flex h-[42px] w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-medium text-ink hover:bg-slate-50 lg:w-auto cursor-pointer"
    >
      <span className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-ink-muted" />
        {range}
      </span>
      <ChevronDown className="h-4 w-4 text-ink-muted" />
    </button>
  );

  const rangeMenu = rangeOpen && (
    <div className="absolute left-0 top-full z-30 mt-2 w-56 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
      {RANGES.map((r) => (
        <button
          key={r}
          type="button"
          onClick={() => {
            onRange(r);
            setRangeOpen(false);
          }}
          className={`block w-full whitespace-nowrap px-4 py-2 text-left text-sm cursor-pointer ${
            range === r ? "bg-primary-50 text-primary-600" : "text-ink hover:bg-slate-50"
          }`}
        >
          {r}
        </button>
      ))}
    </div>
  );

  const bankSelect = (
    <div className="lg:w-44">
      <Select
        value={bank}
        onChange={(e) => onBank(e.target.value)}
        options={banks.map((b) => ({ value: b, label: b }))}
        placeholder="All Banks"
      />
    </div>
  );

  const modeSelect = (
    <div className="lg:w-40">
      <Select value={mode} onChange={(e) => onMode(e.target.value)} options={AUTH_MODE_OPTIONS} placeholder="All Modes" />
    </div>
  );

  const sortSelect = (
    <div className="lg:w-44">
      <Select value={sort} onChange={(e) => onSort(e.target.value)} options={MANDATE_SORT_OPTIONS} />
    </div>
  );

  return (
    <div className="rounded-[10px] border border-line bg-white p-4 shadow-card">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex flex-1 items-center gap-3">
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            prefixIcon={<Search className="h-4 w-4" />}
            placeholder="Search by ref no, customer, mobile, UMRN or product"
            className="flex-1"
          />
          <Button
            variant="secondary"
            onClick={() => setFiltersOpen(true)}
            leftIcon={<SlidersHorizontal className="h-4 w-4" />}
            className="lg:hidden"
          >
            Filters
          </Button>
        </div>

        <div className="hidden flex-wrap items-center gap-3 lg:flex">
          <div className="relative">
            {rangeButton}
            {rangeMenu}
          </div>
          {bankSelect}
          {modeSelect}
          {sortSelect}
          <Button variant="ghost" onClick={onReset} leftIcon={<RotateCcw className="h-4 w-4" />}>
            Reset
          </Button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          {chips.map((c, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-600"
            >
              {c.label}
              <button
                type="button"
                onClick={c.onRemove}
                className="flex h-4 w-4 items-center justify-center rounded-full hover:bg-primary-100 cursor-pointer"
                aria-label="Remove filter"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Modal open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters">
        <div className="space-y-4">
          <div>
            <span className="mb-1.5 block text-[13px] font-medium text-ink-secondary">Date Range</span>
            <Select
              value={range}
              onChange={(e) => onRange(e.target.value)}
              options={RANGES.map((r) => ({ value: r, label: r }))}
            />
            {range === "Custom Range" && (
              <div className="mt-3 space-y-3">
                <DatePicker value={customFrom} onChange={onCustomFrom} placeholder="From date" />
                <DatePicker value={customTo} onChange={onCustomTo} placeholder="To date" />
              </div>
            )}
          </div>
          <div>{bankSelect}</div>
          <div>{modeSelect}</div>
          <div>
            <span className="mb-1.5 block text-[13px] font-medium text-ink-secondary">Sort By</span>
            {sortSelect}
          </div>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              onReset();
              setFiltersOpen(false);
            }}
            leftIcon={<RotateCcw className="h-4 w-4" />}
          >
            Reset Filters
          </Button>
        </div>
      </Modal>
    </div>
  );
}
